import { useEffect, useRef, useState } from 'react';

import { trackPushSilentRegistration } from '#features/analytics/utils/pushPermissionFunnel';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { getPushPermissionState } from '../utils/nativePush';
import type { NativePushState } from '../utils/nativePushState';
import { usePushNotifications } from './usePushNotifications';

/**
 * Молча забрать токен, когда система нам уже всё разрешила.
 *
 * Почему это важнее любой шторки. На Android 12 и ниже разрешения на уведомления НЕ
 * существует — приложение просто регистрируется в FCM, и пуши идут. Никаких диалогов, никакого
 * онбординга; именно поэтому в чужих приложениях никто не видит экранов «включите уведомления».
 * У нас же регистрация висела на шторке: `NotificationsPopover` перерегистрирует токен только
 * при `status === 'ENABLED'`, а этот статус появляется лишь после того, как человек прошёл
 * шторку. Закрыл её словом «Позже» — и токена нет никогда, хотя система ничего не запрещала.
 * На проде это видно прямо: Play насчитывает около 280 активных Android-устройств, а токенов у
 * нас 139.
 *
 * Здесь берётся ровно то, что уже разрешено, и ничего не спрашивается: диалог не поднимается
 * никогда. Поэтому проверка разрешения делается своим вызовом, а не выводится из состояния —
 * `no-token-on-server` считает «не смогли прочитать разрешение» тем же, что «разрешено», и на
 * Android 13+ такая ошибка стоила бы дорого: `registerNativePush` показал бы системный диалог
 * без объяснения и сжёг единственную попытку.
 */
/**
 * Когда в последний раз перерегистрировались, имея рабочий токен. Нужно ради снимка состояния
 * телефона: он ложится в строку токена только при регистрации, а она у здорового устройства не
 * повторяется никогда — и данные, ради которых снимок заводился, не появились бы ни у кого.
 */
const REFRESH_AT_KEY = 'push-token-refreshed-at';

/**
 * Раз в сутки. Реже — и картина устаревает (человек выключил уведомления, система перевела
 * приложение в RESTRICTED), чаще — лишний запрос на каждый запуск ради данных, которые меняются
 * неделями.
 */
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1_000;

const refreshDue = (now: number): boolean => {
  const raw = localStorage.getItem(REFRESH_AT_KEY);
  if (!raw) {
    return true;
  }
  const at = Number.parseInt(raw, 10);
  // Битая отметка и уехавшие вперёд часы читаются как «пора»: цена — один лишний запрос.
  return !Number.isFinite(at) || at > now || now - at >= REFRESH_INTERVAL_MS;
};

export const useSilentPushRegistration = (state: NativePushState) => {
  const { enableNotifications } = usePushNotifications();
  /** Попытка была (успешная или нет). Пока её не было, шторке про токен говорить рано. */
  const [attempted, setAttempted] = useState(false);
  /** Одна попытка на сеанс: повтор при каждом перерисовывании превратился бы в цикл. */
  const started = useRef(false);

  useEffect(() => {
    if (!envConfig.IS_NATIVE || started.current) {
      return;
    }
    /*
     * Две причины сходить в регистрацию, и обе тихие:
     *
     *  - токена на сервере нет, хотя система разрешила, — забираем его молча;
     *  - токен есть, но снимок состояния телефона в его строке успел устареть. Снимок пишется
     *    только регистрацией, а у здорового устройства она не повторяется, — без этого условия
     *    данные о версиях Android не появились бы вовсе.
     */
    const missing = state === 'no-token-on-server';
    const stale = state === 'ok' && refreshDue(Date.now());
    if (!missing && !stale) {
      return;
    }
    started.current = true;

    void (async () => {
      try {
        // Только явное «granted». `prompt` означает, что диалог ещё не показывали, и показать
        // его молча, без объяснения, — худший способ потратить единственный шанс.
        if ((await getPushPermissionState()) !== 'granted') {
          trackPushSilentRegistration('not-allowed');
          return;
        }
        await enableNotifications();
        localStorage.setItem(REFRESH_AT_KEY, String(Date.now()));
        // Считаем и удачу тоже: у большинства телефонов это единственное, что происходит, и без
        // отметки успех выглядел бы как отсутствие событий.
        trackPushSilentRegistration('taken');
      } catch {
        trackPushSilentRegistration('failed');
        // Молчим намеренно: не смогли забрать токен — дальше об этом скажет шторка, а падать
        // из-за фоновой попытки, о которой человек не просил, незачем.
      } finally {
        // Отмечаем в любом случае: не вышло — значит дальше говорит шторка, а молчаливое
        // ожидание оставило бы человека без пушей и без объяснения.
        setAttempted(true);
      }
    })();
  }, [state, enableNotifications]);

  return { attempted };
};
